import { useMemo } from 'react';
import { TableRow, TableCell } from '@mui/material';
import type { TableDescriptor, TableColumnDescriptor } from '@finance/contracts';
import type { TableState, TableFilterState } from './table-state.ts';
import { getVisibleOrderedColumns, getPinnedColumnOffsets } from './table-state.ts';
import type { FilterControlProps, FilterValue } from './filters/filter-registry.ts';
import {
  getFilterControl,
  resolveFilterType,
  createFilterConfig,
} from './filters/filter-registry.ts';
import { tableTokens } from './table-tokens.js';

interface TableFilterRowProps {
  descriptor: TableDescriptor;
  state: TableState;
  onFilterSet: (filter: TableFilterState) => void;
  onFilterRemove: (field: string) => void;
  pinnedLeft: string[];
  pinnedRight: string[];
  selectionEnabled?: boolean;
  hasRowActions?: boolean;
  disabled?: boolean;
  locale?: string;
}

interface ColumnFilterCellProps {
  column: TableColumnDescriptor;
  filter: TableFilterState | null;
  onChange: (filter: TableFilterState | null) => void;
  pinned: 'left' | 'right' | 'center';
  leftOffset?: number;
  rightOffset?: number;
  width?: number;
  disabled?: boolean;
  locale?: string;
}

function ColumnFilterCell({
  column,
  filter,
  onChange,
  pinned,
  leftOffset = 0,
  rightOffset = 0,
  width,
  disabled,
  locale,
}: ColumnFilterCellProps) {
  const filterType = resolveFilterType(column);
  const FilterComponent = getFilterControl(filterType);
  const config = useMemo(() => createFilterConfig(column), [column]);
  const cellWidth = width ?? column.defaultWidth ?? 150;
  const ResolvedFilterControl = useMemo(
    () => (FilterComponent ? FilterComponent(config) : null),
    [FilterComponent, config]
  );

  const handleFilterControlChange = (value: FilterValue | null) => {
    if (!value) onChange(null);
    else onChange({ ...value, field: column.field });
  };

  const filterProps: FilterControlProps = {
    column,
    value: filter,
    onChange: handleFilterControlChange,
    locale: locale ?? 'en',
    disabled,
  };

  return (
    <TableCell
      component="th"
      scope="col"
      data-testid={`table-filter-${column.field}`}
      sx={{
        py: 0.75,
        px: 1,
        position: pinned !== 'center' ? 'sticky' : 'relative',
        left: pinned === 'left' ? `${leftOffset}px` : undefined,
        right: pinned === 'right' ? `${rightOffset}px` : undefined,
        zIndex: pinned !== 'center' ? tableTokens.zIndex.filterRow : undefined,
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: pinned !== 'center' ? tableTokens.pinned.filterBg : 'background.paper',
        width: cellWidth,
        minWidth: cellWidth,
        maxWidth: cellWidth,
      }}
    >
      {ResolvedFilterControl ? <ResolvedFilterControl {...filterProps} /> : null}
    </TableCell>
  );
}

export function TableFilterRow({
  descriptor,
  state,
  onFilterSet,
  onFilterRemove,
  pinnedLeft,
  pinnedRight,
  selectionEnabled,
  hasRowActions,
  disabled,
  locale,
}: TableFilterRowProps) {
  const orderedColumns = useMemo(
    () => getVisibleOrderedColumns(descriptor, state),
    [descriptor, state]
  );
  const columnOffsets = useMemo(
    () => getPinnedColumnOffsets(orderedColumns, pinnedLeft, pinnedRight, state.columnSizes),
    [orderedColumns, pinnedLeft, pinnedRight, state.columnSizes]
  );

  const handleCellChange = (column: TableColumnDescriptor, filter: TableFilterState | null) => {
    if (filter) onFilterSet(filter);
    else onFilterRemove(column.field);
  };

  return (
    <TableRow data-testid="table-filter-row">
      {selectionEnabled && (
        <TableCell component="th" scope="col" padding="checkbox" aria-hidden="true" />
      )}
      {orderedColumns.map((column, index) => {
        const pinned = pinnedLeft.includes(column.id)
          ? 'left'
          : pinnedRight.includes(column.id)
            ? 'right'
            : 'center';
        const offset = columnOffsets[index];
        const cellWidth =
          state.columnSizes.find(s => s.columnId === column.id)?.width ??
          column.defaultWidth ??
          150;
        const filter = state.filters.find(f => f.field === column.field);
        return (
          <ColumnFilterCell
            key={column.id}
            column={column}
            filter={filter ?? null}
            onChange={nextFilter => handleCellChange(column, nextFilter)}
            pinned={pinned}
            leftOffset={offset?.left ?? 0}
            rightOffset={offset?.right ?? 0}
            width={cellWidth}
            disabled={disabled}
            locale={locale}
          />
        );
      })}
      {hasRowActions && (
        <TableCell
          component="th"
          scope="col"
          aria-hidden="true"
          sx={{
            position: 'sticky',
            right: 0,
            zIndex: tableTokens.zIndex.filterRow,
            backgroundColor: tableTokens.pinned.filterBg,
            boxShadow: tableTokens.pinned.shadowLeft,
            minWidth: tableTokens.actionColumn.minWidth,
            width: tableTokens.actionColumn.width,
          }}
        />
      )}
    </TableRow>
  );
}
